<template>
  <transition name="command-palette-transition">
    <v-dialog v-model="isOpen" max-width="600" min-height="500" scrollable>
      <v-card>
        <!-- Title & Search Field -->
        <v-card-title class="py-2 px-3 d-flex align-center">
          <v-text-field
            v-model="searchPattern"
            :placeholder="`${$t('common.nouns.search')} ...`"
            hide-details
            solo
            flat
            prepend-inner-icon="mdi-magnify"
            ref="commandPaletteSearchFiled"
            :loading="isLoading"
            autofocus
          >
          </v-text-field>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Search Results -->
        <v-virtual-scroll v-if="filteredItems.length" :items="filteredItems" :bench="5" height="300" item-height="80">
          <template v-slot:default="{ item, index }">
            <refs-report
              v-if="item.item.type === BackendResourceEnum.REPORT"
              :item="item.item.data"
              :key="index"
              @click="selectItem(item.item)"
            >
              <template v-slot:icon>
                <v-list-item-avatar>
                  <tooltip :text="$t('common.nouns.report')">
                    <v-icon>
                      {{ RefTypeMap.get(item.item.type).icon }}
                    </v-icon>
                  </tooltip>
                </v-list-item-avatar>
              </template>
            </refs-report>
            <refs-vehicle
              v-if="item.item.type === BackendResourceEnum.VEHICLE"
              :item="item.item.data"
              :key="index"
              @click="selectItem(item.item)"
            >
              <template v-slot:icon>
                <v-list-item-avatar>
                  <tooltip :text="$t('common.nouns.vehicle')">
                    <v-icon>
                      {{ RefTypeMap.get(item.item.type).icon }}
                    </v-icon>
                  </tooltip>
                </v-list-item-avatar>
              </template>
            </refs-vehicle>

            <refs-partner
              v-if="item.item.type === BackendResourceEnum.PARTNER"
              :item="item.item.data"
              :key="index"
              @click="selectItem(item.item)"
            >
              <template v-slot:icon>
                <v-list-item-avatar>
                  <tooltip :text="$t('common.nouns.partner')">
                    <v-icon> mdi-handshake-outline </v-icon>
                  </tooltip>
                </v-list-item-avatar>
              </template>
            </refs-partner>

            <refs-person
              v-if="item.item.type === BackendResourceEnum.PERSON"
              :item="item.item.data"
              :key="index"
              @click="selectItem(item.item)"
            >
              <template v-slot:icon>
                <v-list-item-avatar>
                  <v-icon>
                    {{ RefTypeMap.get(item.item.type).icon }}
                  </v-icon>
                </v-list-item-avatar>
              </template>
            </refs-person>

            <!-- the item navigates itself, so the palette only closes - see `NavigationListItem` -->
            <navigation-list-item
              v-if="item.item.type === 'link'"
              :navigationItem="item.item.data"
              :key="index"
              @click="closePalette()"
            ></navigation-list-item>
          </template>
        </v-virtual-scroll>

        <!-- If no results -->
        <v-card-text class="pt-8 pb-0 text-center" flat v-else-if="searchPattern">
          <v-icon size="86">mdi-text-box-search-outline</v-icon>
          <p class="caption overline pt-2">{{ $t("sign.Sign.error.404") }}</p>

          <v-divider> </v-divider>
        </v-card-text>

        <div v-if="!filteredItems.length">
          <v-list>
            <navigation-list-item
              v-for="(searchResult, idx) in favLinks"
              :navigationItem="searchResult"
              :key="idx"
              @click="closePalette()"
            />

            <v-list-item @click="goToQuickLinks()" class="pl-6">
              <v-list-item-content>
                <v-list-item-subtitle>
                  {{ $t("components.QuickLinkList.allFunctions") }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <v-card-actions>
          <small class="text-caption">
            <span class="pr-3">Navigieren <strong class="grey lighten-3 pa-1 rounded">tab</strong> </span>
            <span class="pr-3">Öffnen <strong class="grey lighten-3 pa-1 rounded">enter</strong></span>
            <span class="pr-3">Schließen <strong class="grey lighten-3 pa-1 rounded">esc</strong> </span>
          </small>
          <v-spacer></v-spacer>
          <small class="text-caption ml-2">
            <span>
              Suche <strong class="grey lighten-3 pa-1 rounded">⌘ + k</strong> oder
              <strong class="grey lighten-3 pa-1 rounded">strg + k</strong>
            </span>
          </small>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </transition>
</template>

<script lang="ts">
import RefsPartner from "@/components/utility/RefsPartner.vue";
import RefsPerson from "@/components/utility/RefsPerson.vue";
import RefsReport from "@/components/utility/RefsReport.vue";
import RefsVehicle from "@/components/utility/RefsVehicle.vue";
import Tooltip from "@/components/utility/tooltip.vue";
import NavigationListItem from "@/layouts/navigation/NavigationListItem.vue";
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { PersonRouteNames } from "@/lib/utility/person.go-to-helper";
import { PersonEmailBase } from "@/models/person-email.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { UserModule } from "@/store/modules/me-user.store";
import { NavigationModule } from "@/store/modules/navigation.store";
import { PartnerModule } from "@/store/modules/partner";
import { RefTypeMap } from "@/store/modules/refs.store";
import { PersonSearchModule, ReportSearchModule, VehicleSearchModule } from "@/store/modules/search.store";
import { debounce } from "debounce";
import Fuse from "fuse.js";
import { Component, Vue, Watch } from "vue-property-decorator";

export type CommandPaletteResultType = {
  type: string;
  label: string;
  tags?: string[];
  data?: any;
  action?: () => Promise<any>;
};

@Component({
  components: { Tooltip, RefsReport, RefsVehicle, RefsPartner, RefsPerson, NavigationListItem }
})
export default class CommandPalette extends Vue implements IDialog {
  isOpen = false;
  isLoading = false;
  searchPattern = "";

  debounceTimeout = 300;

  selectedItem: number | undefined = 0;

  @Watch("searchPattern")
  searchQuery() {
    this.debouncedSearch();
  }

  debouncedSearch = debounce(() => this.search(), this.debounceTimeout, false);

  async search() {
    this.isLoading = true;
    const promises: Promise<any>[] = [];

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.REPORT, this.partner.id)) {
        ReportSearchModule.setSearch(this.searchPattern);
        promises.push(ReportSearchModule.fetchPage({ partnerId: this.partner.id }));
      }

      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.VEHICLE, this.partner.id)) {
        VehicleSearchModule.setSearch(this.searchPattern);
        promises.push(VehicleSearchModule.fetchPage({ partnerId: this.partner.id }));
      }

      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partner.id)) {
        PersonSearchModule.setSearch(this.searchPattern);
        promises.push(PersonSearchModule.fetchPage({ partnerId: this.partner.id }));
      }

      await Promise.all(promises);
    } finally {
      this.isLoading = false;
    }
  }

  goToQuickLinks() {
    this.closePalette();

    this.$router.push({ name: "QuickLinks", params: { partnerId: this.partner.id } });
  }

  open(): void {
    this.openPalette();
  }

  close(): void {
    this.closePalette();
  }

  get reports() {
    return ReportSearchModule.entities;
  }

  get vehicles() {
    return VehicleSearchModule.entities;
  }

  get persons() {
    return PersonSearchModule.entities;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get partners() {
    return PartnerModule.partners;
  }

  get BackendResourceEnum() {
    return BackendResourceEnum;
  }

  get RefTypeMap() {
    return RefTypeMap;
  }

  get allQuickLinks() {
    return NavigationModule.quickLinks;
  }

  get favLinks() {
    return this.allQuickLinks.slice(0, 5);
  }

  get isAdmin() {
    return UserModule.isAdmin;
  }

  get mergedItems() {
    const results: CommandPaletteResultType[] = [];

    if (this.isAdmin) {
      results.push(
        ...this.partners.map((v) => ({
          type: BackendResourceEnum.PARTNER,
          label: v.companyName ?? v.companyUsername,
          data: v,
          action: async () =>
            await this.$router.push({
              name: "PartnerDashboard",
              params: { partnerId: v.id }
            })
        }))
      );
    }

    if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.VEHICLE, this.partner.id) && this.vehicles) {
      results.push(
        ...this.vehicles.map((v) => ({
          type: BackendResourceEnum.VEHICLE,
          label: v.displayName ?? v.numberplate,
          tags: [v.identificationnumber].filter((tag): tag is string => tag !== undefined),
          data: v,
          action: async () =>
            await this.$router.push({
              name: "FleetVehicleDetail",
              params: { vehicleId: v.id, partnerId: v.partnerId }
            })
        }))
      );
    }

    if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.REPORT, this.partner.id) && this.reports) {
      results.push(
        ...this.reports.map((r) => ({
          type: BackendResourceEnum.REPORT,
          label: r.numberplate ?? r.title,
          tags: [r.customerContact?.email].filter((tag): tag is string => tag !== undefined),
          data: r,
          action: async () =>
            await this.$router.push({
              name: "PartnerReportsDetailView",
              params: { reportId: r.id, partnerId: r.partnerId }
            })
        }))
      );
    }

    if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partner.id) && this.persons) {
      results.push(
        ...this.persons.map((r) => ({
          type: BackendResourceEnum.PERSON,
          label: r.titleReadable,
          tags: r.emails.map((e: PersonEmailBase) => e.email).filter((tag): tag is string => tag !== undefined),
          data: r,
          action: async () =>
            await this.$router.push({
              name: PersonRouteNames.PERSON_DETAIL,
              params: { personId: r.id, partnerId: r.partnerId }
            })
        }))
      );
    }

    results.push(
      ...this.allQuickLinks.map((l) => ({
        type: "link",
        label: l.title ?? l.name ?? l.path,
        tags: l.tags,
        data: l,
        action: async () => await this.$router.push({ name: l.name, params: { partnerId: this.partner.id } })
      }))
    );

    return results;
  }

  get filteredItems() {
    const fuseOptions = {
      // isCaseSensitive: false,
      // includeScore: false,
      // shouldSort: true,
      // includeMatches: false,
      // findAllMatches: false,
      // minMatchCharLength: 1,
      // location: 0,
      // threshold: 0.6,
      // distance: 100,
      // useExtendedSearch: false,
      // ignoreLocation: false,
      // ignoreFieldNorm: false,
      // fieldNormWeight: 1,
      keys: ["label", "tags"]
    };

    const fuse = new Fuse(this.mergedItems, fuseOptions);

    return fuse.search(this.searchPattern);
  }

  get emptyDataSrc() {
    return AssetRepository.getAsset(false, AssetEnum.loading);
  }

  openPalette() {
    this.isOpen = true;

    this.search();

    this.$nextTick(() => {
      const input: HTMLInputElement = this.$refs.commandPaletteSearchFiled as HTMLInputElement;
      if (input) {
        input.focus();
      } else {
        this.$log.warn("commandPaletteSearchFiled not found");
      }
    });
  }

  closePalette() {
    this.isOpen = false;
    this.searchPattern = "";
  }

  /**
   * Closes before navigating: the palette lives in `TheAppShell`, which outlives the view, so a
   * navigation no longer tears it down. Closing afterwards would leave it open whenever the
   * navigation does not complete - a redundant one to the location the user is already on, one a
   * guard redirects or aborts.
   */
  async selectItem(item: CommandPaletteResultType) {
    this.closePalette();

    if (item.action) {
      await item.action();
    }
  }

  onGlobalKeyDown(e: KeyboardEvent) {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const metaKey = isMac ? e.metaKey : e.ctrlKey;
    const altOrOption = e.altKey || (isMac && e.altKey);
    const pressedK = e.key.toLowerCase() === "k";

    if (metaKey && pressedK && !altOrOption) {
      e.preventDefault();
      this.openPalette();
    }

    if (metaKey && altOrOption && pressedK) {
      e.preventDefault();
      this.openPalette();
    }
  }

  mounted() {
    window.addEventListener("keydown", this.onGlobalKeyDown);
  }

  beforeDestroy() {
    window.removeEventListener("keydown", this.onGlobalKeyDown);
  }
}
</script>

<style scoped>
/* Simple fade/scale transition */
.command-palette-transition-enter-active,
.command-palette-transition-leave-active {
  transition: all 0.2s ease;
}

.command-palette-transition-enter {
  opacity: 0;
  transform: scale(0.95);
}

.command-palette-transition-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
